import { Link } from 'react-router-dom';
import { CheckCircle, MapPin, Phone, Users, Award, Clock } from 'lucide-react';

export default function BaufirmaMeran() {
  return (
    <div className="section-spacing" data-testid="baufirma-meran-page">
      <div className="container-wide">
        {/* Breadcrumb */}
        <nav className="text-sm text-[#64748B] mb-8" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-[#0284C7]">Start</Link>
          <span className="mx-2">/</span>
          <Link to="/haus-und-bau" className="hover:text-[#0284C7]">Haus & Bau</Link>
          <span className="mx-2">/</span>
          <span className="text-[#0F172A]">Baufirma in Meran finden</span>
        </nav>

        {/* Hero */}
        <div className="mb-12">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0F172A] mb-6 font-[var(--font-heading)]">
            Baufirma in Meran finden – worauf achten?
          </h1>
          <p className="text-lg text-[#475569] max-w-3xl">
            Die Wahl der richtigen Baufirma ist entscheidend für den Erfolg deines 
            Bauprojekts. In Meran und Umgebung gibt es viele gute Handwerksbetriebe – 
            aber worauf solltest du achten?
          </p>
        </div>

        {/* Image */}
        <div className="mb-12 rounded-xl overflow-hidden">
          <img 
            src="https://images.unsplash.com/photo-1768464395439-25b2ef151f80?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxpdGFsaWFuJTIwZG9sb21pdGVzJTIwbW91bnRhaW4lMjB2aWxsYWdlJTIwc2NlbmljfGVufDB8fHx8MTc3NDI1Nzg4MHww&ixlib=rb-4.1.0&q=85"
            alt="Panorama Dolomiten Südtirol"
            className="w-full h-64 md:h-96 object-cover"
          />
        </div>

        {/* Content */}
        <div className="advisor-content">
          <h2>Die wichtigsten Kriterien bei der Auswahl</h2>
          
          <div className="grid md:grid-cols-2 gap-6 my-8">
            {[
              {
                icon: MapPin,
                title: 'Regionale Verankerung',
                text: 'Eine Firma aus der Region kennt die lokalen Gegebenheiten, Vorschriften und hat kurze Wege.'
              },
              {
                icon: Award,
                title: 'Referenzen & Erfahrung',
                text: 'Frag nach vergleichbaren Projekten und sprich wenn möglich mit früheren Bauherren.'
              },
              {
                icon: Users,
                title: 'Eigenes Team',
                text: 'Firmen mit eigenem Stammpersonal können Qualität besser kontrollieren als reine Koordinatoren.'
              },
              {
                icon: Clock,
                title: 'Termintreue',
                text: 'Ein realistischer Zeitplan und dessen Einhaltung sind Gold wert. Frag nach Erfahrungen.'
              }
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="bg-[#F8FAFC] rounded-xl p-6 border border-[#E2E8F0]">
                  <div className="w-10 h-10 rounded-lg bg-[#0284C7]/10 flex items-center justify-center mb-4">
                    <Icon className="text-[#0284C7]" size={20} />
                  </div>
                  <h3 className="font-semibold text-[#0F172A] mb-2">{item.title}</h3>
                  <p className="text-sm text-[#475569]">{item.text}</p>
                </div>
              );
            })}
          </div>

          <h2>Checkliste: Vor der Auftragsvergabe</h2>
          <ul className="space-y-3 my-6">
            {[
              'Mehrere Angebote einholen (mindestens 3)',
              'Angebote auf Vollständigkeit prüfen (Leistungsverzeichnis)',
              'Referenzobjekte besichtigen',
              'Versicherungsschutz der Firma prüfen',
              'Zahlungsmodalitäten klar vereinbaren',
              'Bauzeiten und Pönalen schriftlich fixieren',
              'Gewährleistungsfristen beachten'
            ].map((item, index) => (
              <li key={index} className="flex items-start gap-3">
                <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-[#475569]">{item}</span>
              </li>
            ))}
          </ul>

          <h2>Besonderheiten in Südtirol</h2>
          <p>
            Der Baumarkt in Südtirol ist geprägt von einer starken Handwerkstradition. 
            Viele Betriebe sind seit Generationen in Familienhand und legen Wert auf 
            Qualität. Das hat seinen Preis, aber auch seinen Wert.
          </p>
          <p>
            Wichtig zu wissen: In Südtirol gelten sowohl italienische als auch 
            teilweise eigene Bauvorschriften. Eine lokale Firma kennt diese Besonderheiten 
            und kann dich entsprechend beraten.
          </p>

          <h2>Eine bewährte Adresse in Meran</h2>
          <p>
            Wenn du einen erfahrenen Partner für Neubau oder Sanierung in Meran suchst, 
            ist{' '}
            <a href="https://hafner-ec.com" target="_blank" rel="noopener noreferrer">
              hafner-ec.com
            </a>{' '}
            eine gute Anlaufstelle. Das Unternehmen verfügt über jahrelange Erfahrung 
            in der Region und bietet Projekte vom Einfamilienhaus bis zum größeren 
            Wohnbau an.
          </p>

          <div className="cta-box">
            <div className="cta-box-title">Noch unsicher?</div>
            <p className="cta-box-text">
              Unser Entscheidungshelfer zeigt dir, ob Neubau oder Sanierung besser 
              zu deiner Situation passt – bevor du eine Firma suchst.
            </p>
            <Link to="/tippspiele/neubau-oder-sanierung" className="text-[#0284C7] font-medium hover:underline">
              Zum Entscheidungshelfer: Neubau oder Sanierung? →
            </Link>
          </div>

          {/* Internal Links */}
          <div className="internal-links">
            <div className="internal-links-title">Weiterlesen:</div>
            <div className="internal-links-list">
              <Link to="/ratgeber/hausbau-in-suedtirol" className="internal-link">
                Hausbau in Südtirol
              </Link>
              <Link to="/haus-und-bau/was-kostet-eine-sanierung" className="internal-link">
                Was kostet eine Sanierung?
              </Link>
              <Link to="/tippspiele/was-kostet-dieses-haus" className="internal-link">
                Was kostet dieses Haus?
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
